import {
  TWorkflowDefinition,
  TWorkflowStepDefinition,
  TWorkflowStepTransition,
  TWorkflowTriggerDefinition,
  WorkflowTriggerType,
} from '@commenda/types';
import { ExpressionType } from '@commenda/types/src/workflow/dsl';
import { Edge, Node } from '@xyflow/react';
import { TRIGGER_NODE_TYPE, TTriggerNodeData } from '../types/node';

export class FlowToDSLAdapter {
  private static nodeToStep(node: Node): TWorkflowStepDefinition {
    const { label, position, ...stepData } = node.data;
    return {
      ...stepData,
      ui: {
        reactFlow: {
          position: node.position,
        },
      },
    } as TWorkflowStepDefinition;
  }

  private static triggerNodeToTrigger(
    node: Node<TTriggerNodeData>
  ): TWorkflowTriggerDefinition {
    const data = node.data;
    const base = {
      id: data.id,
      type: data.triggerType,
      name: data.name,
      ui: { reactFlow: { position: node.position } },
    };

    if (data.triggerType === WorkflowTriggerType.INTERNAL_EVENT) {
      return {
        ...base,
        type: WorkflowTriggerType.INTERNAL_EVENT,
        eventType: data.eventType ?? '',
      };
    }

    return { ...base, type: WorkflowTriggerType.MANUAL };
  }

  private static edgeToTransition(edge: Edge): TWorkflowStepTransition {
    return {
      id: edge.id,
      fromStepId: edge.source,
      toStepId: edge.target,
      ...(edge.data?.condition
        ? {
            condition: {
              type: ExpressionType.JSONATA,
              src: edge.data?.condition as string,
            },
          }
        : {}),
      label: typeof edge.label === 'string' ? edge.label : undefined,
      ui: {
        reactFlow: {
          handle: {
            sourceHandle: edge.sourceHandle,
            targetHandle: edge.targetHandle,
          },
        },
      },
    };
  }

  static flowToDsl(
    nodes: Node[],
    edges: Edge[],
    workflowId: string,
    workflowName: string
  ): TWorkflowDefinition {
    const stepNodes = nodes.filter((n) => n.type !== TRIGGER_NODE_TYPE);
    const triggerNodes = nodes.filter((n) => n.type === TRIGGER_NODE_TYPE);

    return {
      id: workflowId,
      name: workflowName,
      triggers: triggerNodes.map((n) =>
        FlowToDSLAdapter.triggerNodeToTrigger(n as Node<TTriggerNodeData>)
      ),
      steps: stepNodes.map(FlowToDSLAdapter.nodeToStep),
      stepTransitions: edges.map(FlowToDSLAdapter.edgeToTransition),
    };
  }

  static extractNodePositions(
    nodes: Node[]
  ): Map<string, { x: number; y: number }> {
    const positions = new Map();
    nodes.forEach((node) => {
      positions.set(node.id, { x: node.position.x, y: node.position.y });
    });
    return positions;
  }
}
